import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, FilePlus, FileText, Archive, AlertTriangle, Truck, LogOut, Users } from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/create-record', label: 'Create Record', icon: FilePlus },
  { path: '/active-records', label: 'Active Records', icon: FileText },
  { path: '/archive', label: 'Archive', icon: Archive },
  { path: '/email-exceptions', label: 'Email Exceptions', icon: AlertTriangle },
  { path: '/staff', label: 'Staff', icon: Users, adminOnly: true },
  { path: '/user-profiles', label: 'User Profiles', icon: Users, adminOnly: true },
];

export default function OfficeLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="office-layout">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-6 h-16 flex items-center">
          {/* Logo - left */}
          <div className="flex-shrink-0">
            <img
              src="/cargodash-logo.svg"
              alt="CargoDash"
              className="h-10 cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="logo"
            />
          </div>

          {/* Nav - centred */}
          <nav className="flex-1 hidden md:flex items-center justify-center gap-1">
            {navItems
              .filter(item => !item.adminOnly || user?.role === 'admin')
              .map(({ path, label, icon: Icon }) => {
                const isActive = location.pathname === path;
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-[#0D9488] bg-teal-50'
                        : 'text-[#4B5563] hover:text-[#1A1A2E] hover:bg-gray-50'
                    }`}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                );
              })}
          </nav>

          {/* Right side */}
          <div className="flex-shrink-0 flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-[#1A1A2E]">{user?.name}</p>
              <p className="text-xs text-[#6B7280] capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-teal-100 text-[#0D9488] flex items-center justify-center text-sm font-bold">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <button
              onClick={logout}
              className="p-2 text-[#6B7280] hover:text-[#DC2626] transition-colors rounded-md hover:bg-gray-50"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1440px] mx-auto px-6 py-6">
        {children}
      </main>
    </div>
  );
}
